import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { PageEvent } from '@angular/material/paginator';
import { Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import jsPDF from 'jspdf';
import * as moment from 'moment';
import { ClientCatalogItem, ClientCatalogService } from 'src/app/services/client-catalog.service';
import { SnackBarService } from 'src/app/services/snack-bar.service';
import { environment } from 'src/environments/environment';
import { DeliveryOutputDto, DeliveryPayingService, DeliveryService, StoreOutputDto, StorePayingService, StoreService } from 'src/generated_client';

@Component({
  selector: 'app-consulta-liquidaciones',
  templateUrl: './consulta-liquidaciones.component.html',
  styleUrls: ['./consulta-liquidaciones.component.css']
})
export class ConsultaLiquidacionesComponent implements OnInit, OnDestroy {

  displayedColumns: string[] = ['id', 'date', 'total', 'net', 'commission', 'liquidacion', 'pdf'];
  dataSource: any = [];
  searched: boolean = false;

  isClient: boolean = true;
  isClientServices: boolean = false;
  title: string = 'Liquidación clientes';
  isRedelivery = environment.featureManagement.reDelivery;
  isCoopExpress = environment.featureManagement.coopExpress;

  allStores: StoreOutputDto[] = [];
  allDeliveries: DeliveryOutputDto[] = [];
  allServiceClients: ClientCatalogItem[] = [];
  private readonly clientServiceNames = ['Mensajeria', 'Punto Fijo'];
  private serviceClientsReload$ = new Subject<void>();
  private destroy$ = new Subject<void>();
  clientLiquidationsSeq = 0;
  clientLiqPageSize = 20;
  clientLiqPageIndex = 0;
  clientLiqTotalCount = 0;
  dateFrom: any;
  dateTo: any;

  liquidacionesForm: FormGroup = this.fb.group({
    client: [''],
    delivery: [''],
  })

  constructor(
    public storeService: StoreService,
    public storePayingService: StorePayingService,
    public deliveryService: DeliveryService,
    public deliveryPayingService: DeliveryPayingService,
    public snackBar: SnackBarService,
    private router: Router,
    private fb: FormBuilder,
    private http: HttpClient,
    private clientCatalog: ClientCatalogService,
  ) {
    // switchMap cancela la carga anterior al cambiar de vista: no llegan
    // respuestas fuera de orden al catálogo de clientes de servicios.
    this.serviceClientsReload$.pipe(
      switchMap(() => {
        this.allServiceClients = [];
        return this.clientCatalog.getAll(this.clientServiceNames);
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: clients => this.allServiceClients = clients,
      error: error => {
        this.allServiceClients = [];
        const message = typeof error.error === 'string'
          ? error.error
          : error.error?.error || 'No se pudieron cargar los clientes.';
        this.snackBar.openSnackBar(message, 'Aceptar');
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private toArray<T>(response: any): T[] {
    if (Array.isArray(response)) {
      return response;
    }

    const candidates = [response?.data, response?.items, response?.result, response?.values];
    const firstArray = candidates.find(Array.isArray);

    return firstArray ?? [];
  }

  ngOnInit() {
    this.searched = false;
    this.dataSource = [];
    this.clientLiquidationsSeq++;
    this.clientLiqPageIndex = 0;
    this.clientLiqTotalCount = 0;
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    // CoopExpress opera Mensajería y Punto Fijo: la vista por defecto es Mensajería,
    // salvo que el usuario haya elegido explícitamente la liquidación de clientes
    // de servicios.
    if (!this.isRedelivery && this.isCoopExpress && !this.isClientServices) {
      this.isClient = false;
      this.isClientServices = false;
      this.title = 'Liquidación mensajería';
    }
    this.displayedColumns = this.isClientServices
      ? ['id', 'numero', 'date', 'total', 'net', 'commission', 'liquidacion', 'pdf']
      : ['id', 'date', 'total', 'net', 'commission', 'liquidacion', 'pdf'];

    if (this.isClientServices) {
      this.loadServiceClients();
    } else if (this.isClient) {
      this.storeService.apiStoreGetAllStoresGet().subscribe(response => {
        this.allStores = this.toArray<StoreOutputDto>(response);
      });
    } else {
      this.deliveryService.apiDeliveryGetAllGet().subscribe(response => {
        this.allDeliveries = this.toArray<DeliveryOutputDto>(response);
      })
    }
  }

  private loadServiceClients(): void {
    this.serviceClientsReload$.next();
  }

  inpuDateFrom(event: any) {
    this.dateFrom = event.value;
  }

  inpuDateTo(event: any) {
    this.dateTo = event.value;
  }

  search(form: any) {
    if (this.isClientServices && !form.client) {
      this.snackBar.openSnackBar('Debe seleccionar un cliente.', 'Aceptar');
      return;
    }

    this.searched = true;
    const dateTo = this.dateTo ? moment(this.dateTo).endOf('day').toDate() : undefined;

    if (this.isClientServices) {
      this.loadClientLiquidations(Number(form.client), 0);
    } else if (this.title === 'Liquidación clientes') {
      this.storePayingService.apiStorePayingGetStoreLiquidationsCdStoreGet(form.client, this.dateFrom, dateTo).subscribe(response => {
        this.dataSource = this.mapResponseRows(response);
      });
    } else if (this.title === 'Liquidación cadetes') {
      this.deliveryPayingService.apiDeliveryPayingDeliveryLiquidationByDateGet(form.delivery, this.dateFrom, dateTo).subscribe(response => {
        this.dataSource = this.mapResponseRows(response);
      });
    } else if (this.title === 'Liquidación mensajería') {
      this.deliveryPayingService.apiDeliveryPayingParticularOrderLiquidationByDateGet(form.delivery, this.dateFrom, dateTo).subscribe(response => {
        this.dataSource = this.mapResponseRows(response);
      });
    }
  }

  private mapResponseRows(response: any): any[] {
    const items = this.toArray<any>(response);

    // Compatibilidad de nombres: el DTO histórico de liquidación de cadetes expone
    // QtComission (con "s"); los DTOs nuevos usan QtComision. Se prioriza el valor
    // real de comisión. Para "Liquidación mensajería" ya no se pisa con qtPaying:
    // la comisión real viene del comprobante emitido (si un registro viejo la tiene
    // en 0, la UI muestra 0 y el PDF "-", que es un problema de datos y no de vista).
    return items.map(item => ({
      ...item,
      qtComision: item.qtComision !== undefined ? item.qtComision : item.qtComission,
    }));
  }

  private loadClientLiquidations(cdClient: number, pageIndex: number): void {
    const seq = ++this.clientLiquidationsSeq;
    this.clientLiqPageIndex = pageIndex;
    const url = `${environment.apiBaseUrl}/api/ClientLiquidation/GetClientLiquidations`;
    const params: any = {
      cdClient,
      pageNumber: (pageIndex + 1).toString(),
      pageSize: this.clientLiqPageSize.toString(),
    };
    const dateTo = this.dateTo ? moment(this.dateTo).endOf('day').toDate() : undefined;
    if (this.dateFrom) {
      params.dtFrom = moment(this.dateFrom).startOf('day').toDate().toISOString();
    }
    if (dateTo) {
      params.dtTo = dateTo.toISOString();
    }

    this.http.get<any>(url, { params }).subscribe({
      next: (response) => {
        if (seq !== this.clientLiquidationsSeq) {
          return;
        }
        this.dataSource = this.mapResponseRows(response);
        this.clientLiqTotalCount = response?.totalCount ?? this.dataSource.length;
      },
      error: (error) => {
        if (seq !== this.clientLiquidationsSeq) {
          return;
        }
        this.dataSource = [];
        this.clientLiqTotalCount = 0;
        const message = typeof error.error === 'string'
          ? error.error
          : error.error?.error || 'Error al consultar las liquidaciones';
        this.snackBar.openSnackBar(message, 'Aceptar');
      }
    });
  }

  onClientLiqPage(event: PageEvent): void {
    const cdClient = Number(this.liquidacionesForm.get('client')?.value);
    if (!cdClient) {
      return;
    }
    const pageSizeChanged = event.pageSize !== this.clientLiqPageSize;
    this.clientLiqPageSize = event.pageSize;
    this.loadClientLiquidations(cdClient, pageSizeChanged ? 0 : event.pageIndex);
  }

  onClientSelectionChange(): void {
    this.clientLiquidationsSeq++;
    this.dataSource = [];
    this.searched = false;
    this.clientLiqPageIndex = 0;
    this.clientLiqTotalCount = 0;
  }

  private resetClientSelection(): void {
    const client = this.liquidacionesForm.get('client');
    client?.setValue('');
    client?.markAsUntouched();
  }

  clientView() {
    this.isClient = true;
    this.isClientServices = false;
    this.title = 'Liquidación clientes';
    this.resetClientSelection();
    this.ngOnInit();
  }

  cadeteView() {
    this.isClient = false;
    this.isClientServices = false;
    this.title = 'Liquidación cadetes';
    this.ngOnInit();
  }

  particularView() {
    this.isClient = false;
    this.isClientServices = false;
    this.title = 'Liquidación mensajería';
    this.ngOnInit();
  }

  clientServicesView() {
    this.isClient = false;
    this.isClientServices = true;
    this.title = 'Liquidación clientes de servicios';
    this.resetClientSelection();
    this.ngOnInit();
  }

  exportPDF(element: any) {
    const date = moment(element.dtFecha);
    const doc = new jsPDF();
    let y = 10;

    if (this.isClientServices) {
      doc.text("Cliente: " + (element.dsClient || '-'), 10, y);
      y += 10;
      doc.text("ID: " + element.cdClientPaying, 10, y);
      y += 10;
      doc.text("N° Liquidación: " + (element.dsNumeroLiquidacion || '-'), 10, y);
      y += 10;
    } else if (this.isClient) {
      doc.text("Cliente: " + element.dsStoreName, 10, y);
      y += 10;
      doc.text("ID: " + element.cdStorePaying, 10, y);
      y += 10;
    } else {
      doc.text("ID: " + element.cdDriverPaying, 10, y);
      y += 10;
    }

    doc.text("Fecha: " + date.toDate().toLocaleString(), 10, y);
    y += 10;
    doc.text("Total: " + element.qtTotal, 10, y);
    y += 10;
    doc.text("Neto: " + element.qtNeto, 10, y);
    y += 10;
    if (element.qtComision) {
      doc.text("Comisión: " + element.qtComision, 10, y);
    } else {
      doc.text("Comisión: -", 10, y);
    }
    y += 10;
    doc.text("Liquidación: " + element.qtPaying, 10, y);
    if (this.isClientServices && element.dsObservacion) {
      y += 10;
      doc.text("Observaciones: " + element.dsObservacion, 10, y);
    }
    doc.save("liquidacion.pdf");
  }

}
